import React from 'react';
import { determineAssetTransformer } from '../../transformers/determineAssetTransformer';
import { contentfulContentWrapperToArticleProductWrapper } from '../../transformers/contentfulContentWrapperToArticleProductWrapper';
import { ArticleProductWithProvider } from '../ArticleProduct/ArticleProduct';
import { contentfulProductSummaryToArticleProductProps } from '../../transformers/contentfulProductSummaryToArticleProductProps';
import { determineCarouselTransformer } from '../../transformers/determineCarouselTransformer';
import { ProductGrid } from '@jbhi-fi/components/src/ProductGrid/ProductGrid';
import { ProductGridTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ProductGridTransformer';
import { TimelineItem, TimelineWrapper } from '../TimelineWrapper/TimelineWrapper';
import { contentfulVideoTimestampToTimelineItemProps } from '../../transformers/contentfulVideoTimestampToTimelineItemProps';
import { contentfulVideoTimelineToTimelineWrapperProps } from '../../transformers/contentfulVideoTimelineToTimelineWrapperProps';
import { ArticleRichTextLayoutBlockStyleProps } from './ArticleRichTextLayoutBlock/ArticleRichTextLayoutBlock';
import { ArticleProductWrapper } from '../ArticleProductWrapper/ArticleProductWrapper';
import { StackYoutubePlayer } from '@jbhi-fi/components/src/StackYouTubePlayer/StackYouTubePlayer';
import { contentfulYoutubeToYoutubePlayerProps } from '@jbhi-fi/components/src/Contentful/transformers/contentfulYouTubeToYouTubePlayerProps';
import { FormatForm } from '../FormatForm/FormatForm';
import { contentfulApiFormAssemblyFormToFormatForm } from '../../transformers/contentfulApiFormAssemblyFormToFormatForm';
import { CompetitionStatusGrid } from '../CompetitionStatusGrid/CompetitionStatusGrid';
import { contentfulApiCompetitionGridToCompetitionGridProps } from '../../transformers/contentfulApiCompetitionGridToCompetitionGridProps';
import { contentfulApiContentBannerToContentBannerProps } from '../../transformers/contentfulApiContentBannerToContentBannerProps';
import { ContentBanner } from '../ContentBanner/ContentBanner';
import { ContentfulIcon } from '@jbhi-fi/apis/src/contentful/types';
import { LoadingSkeletonProps } from '@jbhi-fi/components/src/LoadingSkeleton/Base/Base';
import { ButtonTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ButtonTransformer';
import { Icon, IconName } from '@jbhi-fi/lanyard-ui/components/Icon';
import { AlignedButton } from '@jbhi-fi/components/src/AlignedButton/AlignedButton';
import { PreezieFinder } from '../../../preezieFinder/PreezieFinder';
import { contentfulApiPreezieBannerToPreezieFinderProps } from '../../../preezieFinder/contentfulApiPreezieBannerToPreezieFinderProps';
import { ListingBlock } from '@jbhi-fi/components/src/ListingBlock';
import { ListingBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ListingBlockTransformer';
import { SideBySide } from '@jbhi-fi/components/src/SideBySide/SideBySide';
import { SideBySideTransformer } from '@jbhi-fi/components/src/Contentful/transformers/SideBySideTransformer';
import { ScrollTarget } from '@jbhi-fi/components/src/ScrollTarget/ScrollTarget';
import { AnchorTransformer } from '@jbhi-fi/components/src/Contentful/transformers/AnchorTransformer';
import { TableOfContents } from '@jbhi-fi/components/src/TableOfContents/TableOfContents';
import { TableOfContentsTransformer } from '@jbhi-fi/components/src/Contentful/transformers/TableOfContentsTransformer';
import { ButtonBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ButtonBlockTransformer';
import { MultiColumnEntry } from '@jbhi-fi/components/src/MultiColumnEntry/MultiColumnEntry';
import { MultiColumnEntryTransformer } from '@jbhi-fi/components/src/Contentful/transformers/MultiColumnEntryTransformer';
import { MultiColumnBlock } from '@jbhi-fi/components/src/MultiColumnBlock/MultiColumnBlock';
import { MultiColumnBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/MultiColumnBlockTransformer';
import { Callout } from '@jbhi-fi/components/src/Callout/Callout';
import { CalloutTransformer } from '@jbhi-fi/components/src/Contentful/transformers/CalloutTransformer';
import { PerksButtonBlock } from '../PerksButtonBlock/PerksButtonBlock';
import { ContentfulPagesList } from '@jbhi-fi/components/src/ContentfulPagesList/ContentfulPagesList';
import { PageListingTransformer } from '@jbhi-fi/components/src/Contentful/transformers/PageListingTransformer';
import { TabsBlock } from '@jbhi-fi/components/src/TabsBlock/TabsBlock';
import { ProductBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ProductBlockTransformer';
import { HeroBanner } from '@jbhi-fi/components/src/HeroBanner/HeroBanner';
import { HeroBannerCarousel } from '@jbhi-fi/components/src/HeroBannerCarousel/HeroBannerCarousel';
import { LeaderboardBannerTransformer } from '@jbhi-fi/components/src/Contentful/transformers/LeaderboardBannerTransformer';

export type EntryTransformer = {
  OutputComponent: React.FC<any>;
  transformer: (entity: any) => any;
  loadingProps?: LoadingSkeletonProps;
  layoutBlockProps?: ArticleRichTextLayoutBlockStyleProps | ((entry: any) => ArticleRichTextLayoutBlockStyleProps);
};

export const entryTransformersMap: Map<string, EntryTransformer | ((entityFields: any) => EntryTransformer)> = new Map(
  Object.entries({
    button: {
      OutputComponent: AlignedButton,
      transformer: ButtonTransformer.toAlignedButtonProps,
      loadingProps: { height: '20px', width: '200px', shouldShimmer: false },
      layoutBlockProps: { paddingTop: 2 },
    },
    carousel: determineCarouselTransformer,
    Asset: determineAssetTransformer,
    featuredProductWrapper: {
      OutputComponent: ArticleProductWrapper,
      transformer: contentfulContentWrapperToArticleProductWrapper,
      loadingProps: { height: { xs: '300px', md: '500px' } },
      layoutBlockProps: {
        paddingLeft: { mobile: 0 },
        paddingRight: { mobile: 0 },
        paddingTop: 15,
        paddingBottom: 1,
      },
    },
    productSummary: {
      OutputComponent: ArticleProductWithProvider,
      transformer: contentfulProductSummaryToArticleProductProps,
    },
    videoTimeline: {
      loadingProps: { height: { xs: '300px', md: '500px' } },
      layoutBlockProps: { paddingTop: 10 },
      OutputComponent: TimelineWrapper,
      transformer: contentfulVideoTimelineToTimelineWrapperProps,
    },
    videoTimestamp: {
      layoutBlockProps: { paddingTop: 0, paddingLeft: 0, paddingRight: 0 },
      OutputComponent: TimelineItem,
      transformer: contentfulVideoTimestampToTimelineItemProps,
    },
    icon: {
      layoutBlockProps: { paddingTop: 0, paddingBottom: 0, paddingRight: 0, paddingLeft: 0 },
      OutputComponent: Icon,
      transformer: (entry: ContentfulIcon) => ({
        name: entry?.fields?.name as IconName,
        fallback: null,
        placeholder: null,
      }),
    },
    youTube: {
      layoutBlockProps: { paddingTop: 10 },
      loadingProps: { height: '560px', width: '315px' },
      OutputComponent: StackYoutubePlayer,
      transformer: contentfulYoutubeToYoutubePlayerProps,
    },
    formAssemblyForm: {
      layoutBlockProps: { paddingTop: 7.5 },
      loadingProps: { height: '700px', width: '315px' },
      OutputComponent: FormatForm,
      transformer: contentfulApiFormAssemblyFormToFormatForm,
    },
    competitionGrid: {
      OutputComponent: CompetitionStatusGrid,
      transformer: contentfulApiCompetitionGridToCompetitionGridProps,
    },
    contentBanner: {
      OutputComponent: ContentBanner,
      transformer: contentfulApiContentBannerToContentBannerProps,
    },
    listingBlock: {
      OutputComponent: ListingBlock,
      layoutBlockProps: ListingBlockTransformer.toArticleRichTextLayoutBlockProps,
      transformer: ListingBlockTransformer.toListingBlockProps,
    },
    preezieBanner: {
      OutputComponent: PreezieFinder,
      transformer: contentfulApiPreezieBannerToPreezieFinderProps,
    },
    sideBySide: {
      OutputComponent: SideBySide,
      transformer: SideBySideTransformer.toSideBySideProps,
    },
    productGrid: {
      OutputComponent: ProductGrid,
      transformer: ProductGridTransformer.toProductGridProps,
    },
    anchor: {
      layoutBlockProps: { paddingTop: 0, paddingBottom: 0, paddingRight: 0, paddingLeft: 0 },
      OutputComponent: ScrollTarget,
      transformer: AnchorTransformer.toScrollTargetProps,
    },
    tableOfContents: {
      OutputComponent: TableOfContents,
      transformer: TableOfContentsTransformer.toTableOfContentsProps,
    },
    buttonBlock: {
      OutputComponent: PerksButtonBlock,
      transformer: ButtonBlockTransformer.toButtonBlockProps,
    },
    multiColumnEntry: {
      OutputComponent: MultiColumnEntry,
      transformer: MultiColumnEntryTransformer.toMultiColumnEntryProps,
      layoutBlockProps: { paddingBottom: 7.5 },
    },
    multiColumnBlock: {
      OutputComponent: MultiColumnBlock,
      transformer: MultiColumnBlockTransformer.toMultiColumnBlockProps,
      layoutBlockProps: { paddingBottom: 10 },
    },
    callout: {
      OutputComponent: Callout,
      transformer: CalloutTransformer.toCalloutProps,
    },
    pageListing: {
      OutputComponent: ContentfulPagesList,
      transformer: PageListingTransformer.toContentfulPagesListProps,
    },
    productBlock: {
      OutputComponent: TabsBlock,
      transformer: ProductBlockTransformer.toTabsBlockProps,
    },
    leaderboardBanner: {
      OutputComponent: HeroBanner,
      transformer: LeaderboardBannerTransformer.toHeroBannerProps,
    },
  })
);
